import * as React from 'react';

import classnames from 'classnames';
import moment from 'moment';
import AutoSizer from 'react-virtualized/dist/es/AutoSizer';
import Grid from 'react-virtualized/dist/es/Grid';
import _ from 'underscore';
// @ts-expect-error
import capitalize from 'underscore.string/capitalize';

import {jsonToModelPropArray} from 'nutshell-core';

import {api} from 'nutshell-core/api';
import type {EventApiResponse, EventModel} from 'nutshell-core/types';
import {getMultipleEntityType} from 'nutshell-core/utils/get-multiple-entity-type';
import {getSingularEntityType} from 'nutshell-core/utils/get-singular-entity-type';

import {ActionMenu} from 'shells/action-menu/action-menu';
import {Entity} from 'shells/entity/entity';
import {ChevronDownIcon, ChevronUpIcon} from 'shells/icon';
import {MainContentContainer} from 'shells/layout/main-content-container';
import {Link} from 'shells/link/link';
import {measureScrollbarWidth} from 'shells/utils/scrollbar';

import {PageTitleBar} from '../layout';

import {TrashEmpty} from './trash-empty';
import {TrashFilters} from './trash-filters';
import type {TrashFilterOption} from './trash-filters';
import {TrashLoading} from './trash-loading';

import styles from './trash.module.css';

const COLUMN_HEADERS = ['Name', 'Type', 'Deleted by', 'Deleted time', 'Restore all'];

type RecycleBinSortOption = 'change_time' | '-change_time';

type State = {
    isLoading: boolean;
    isFirstLoad: boolean;
    models: EventModel[];
    sort: RecycleBinSortOption;
    selectedFilter: TrashFilterOption;
    undeletedModelIds: string[];
    errorMessage?: string;
    scrollbarWidth: number;
};

export class Trash extends React.PureComponent<Record<any, any>, State> {
    grid: any;
    // @ts-expect-error
    gridWidth: number;

    constructor() {
        // @ts-expect-error
        super();
        this.state = {
            isFirstLoad: true,
            isLoading: false,
            models: [],
            sort: 'change_time',
            selectedFilter: 'all',
            undeletedModelIds: [],
            scrollbarWidth: measureScrollbarWidth(),
        };
    }

    componentDidUpdate(prevProps: Record<any, any>, prevState: State) {
        if (prevState.isLoading !== this.state.isLoading && this.grid) {
            this.grid.recomputeGridSize();
        }

        // Fetch new items if the sort or filter change
        if (
            prevState.sort !== this.state.sort ||
            prevState.selectedFilter !== this.state.selectedFilter
        ) {
            this.fetchItems(this.state.selectedFilter, this.state.sort);
        }
    }

    UNSAFE_componentWillMount() {
        this.fetchItems(this.state.selectedFilter, this.state.sort);
    }

    render() {
        return (
            <MainContentContainer minWidth={968}>
                <PageTitleBar
                    title='Trash'
                    subheaderPrimaryText='Items will be automatically deleted after 30 days.'
                    buttons={
                        <TrashFilters
                            selectedFilter={this.state.selectedFilter}
                            onChange={this.handleFilterChange}
                        />
                    }
                />
                <div className={styles['column-headers']}>
                    {COLUMN_HEADERS.map((label, index) => {
                        if (this.state.isFirstLoad) {
                            return undefined;
                        }

                        if (label === 'Deleted time') {
                            return (
                                <div
                                    key={label}
                                    onClick={this.handleToggleSort}
                                    style={{width: this.getColumnWidth({index})}}
                                    className={styles['column-header-sortable']}
                                >
                                    {label}
                                    <div className={styles['sort-icon']}>
                                        {this.state.sort === 'change_time' ? (
                                            <ChevronDownIcon />
                                        ) : (
                                            <ChevronUpIcon />
                                        )}
                                    </div>
                                </div>
                            );
                        } else if (label === 'Restore all') {
                            const count = this.state.models.length;
                            if (count === 0) {
                                return undefined;
                            }

                            let itemText;
                            if (this.state.selectedFilter === 'all') {
                                itemText = count === 1 ? 'item' : 'items';
                            } else {
                                itemText =
                                    count === 1
                                        ? getSingularEntityType(this.state.selectedFilter)
                                        : getMultipleEntityType(this.state.selectedFilter);
                            }

                            return (
                                <div
                                    key={label}
                                    style={{width: this.getColumnWidth({index})}}
                                    className={classnames(
                                        styles['column-header'],
                                        styles['options-header']
                                    )}
                                >
                                    <Link variant='primary' onClick={this.handleUndeleteAllAction}>
                                        Restore {count} {itemText}
                                    </Link>
                                </div>
                            );
                        }

                        return (
                            <div
                                key={label}
                                style={{width: this.getColumnWidth({index})}}
                                className={styles['column-header']}
                            >
                                {label}
                            </div>
                        );
                    })}
                </div>
                <div className={styles.container}>
                    <AutoSizer onResize={this.handleResize}>
                        {({height, width}) => {
                            this.gridWidth = width;

                            return (
                                <Grid
                                    ref={(c) => {
                                        this.grid = c;
                                    }}
                                    cellRenderer={this.renderCell}
                                    columnWidth={this.getColumnWidth}
                                    columnCount={5}
                                    height={height}
                                    width={width}
                                    rowHeight={50}
                                    noContentRenderer={this.renderSpecialState}
                                    rowCount={this.state.models.length}
                                />
                            );
                        }}
                    </AutoSizer>
                </div>
            </MainContentContainer>
        );
    }

    fetchItems = (selectedFilter: TrashFilterOption, sort: RecycleBinSortOption) => {
        this.setState({
            isLoading: true,
            models: [],
        });

        const query =
            selectedFilter && selectedFilter !== 'all'
                ? {sort, filter: {types: selectedFilter}}
                : {sort};

        api.get('events/deleted', query)
            // @ts-expect-error
            .then((response) => response.json())
            .then((response: EventApiResponse) => {
                const models: EventModel[] = jsonToModelPropArray(response.events, response);

                this.setState({
                    models: models,
                    isLoading: false,
                    isFirstLoad: false,
                    errorMessage: undefined,
                });
            })
            .catch(() => {
                this.setState({
                    isFirstLoad: false,
                    isLoading: false,
                    errorMessage: 'We couldn’t load trash items right now.',
                });
            });
    };

    renderSpecialState = () => {
        if (this.state.isLoading) {
            return <TrashLoading />;
        }

        return (
            <TrashEmpty
                selectedFilter={this.state.selectedFilter}
                errorMessage={this.state.errorMessage}
            />
        );
    };

    renderCell = ({
        columnIndex,
        rowIndex,
        key,
        style,
    }: {
        columnIndex: number;
        rowIndex: number;
        key: string;
        style: any;
    }) => {
        const modelForCell = this.getModelForRow(rowIndex);
        let cellStyles =
            rowIndex % 2 === 0
                ? _.extend(style, {backgroundColor: '#fff'})
                : _.extend(style, {backgroundColor: '#f8f7f7'});

        if (this.state.undeletedModelIds.includes(modelForCell.id)) {
            cellStyles = _.extend(style, {backgroundColor: '#ecfffa'});
        }

        return (
            <div key={key} style={cellStyles} className={styles['cell-container']}>
                {this.getCellContents(columnIndex, rowIndex)}
            </div>
        );
    };

    getColumnWidth = ({index}: {index: number}) => {
        if (index === 0) {
            return (this.gridWidth / 6) * 2 - this.state.scrollbarWidth;
        }

        return this.gridWidth / 6;
    };

    handleResize = _.debounce(() => {
        if (this.grid) {
            this.grid.recomputeGridSize();
        }
    }, 200);

    getCellContents = (columnIndex: number, rowIndex: number): React.ReactElement<any> => {
        const model = this.getModelForRow(rowIndex);
        const payload = model.payloads[0];

        if (columnIndex === 0) {
            return (
                <div className={styles['entity-container']}>
                    <Entity {...payload} />
                </div>
            );
        } else if (columnIndex === 1) {
            return <div>{capitalize(getSingularEntityType(payload.type))}</div>;
        } else if (columnIndex === 2) {
            return <div>{model.actor.name}</div>;
        } else if (columnIndex === 3) {
            return <div>{moment.unix(model.createdTime).format('MMM DD, YYYY, h:mma')}</div>;
        } else {
            const isUndeleted = this.state.undeletedModelIds.includes(model.id);

            if (isUndeleted) {
                return <div className={styles['redelete-link']}>Restored</div>;
            }

            const menuOption = [
                {
                    label: 'Permanently delete',
                    handleSelect: () => this.handlePermaneteDelete(model),
                },
            ];

            return (
                <div className={styles['options-container']}>
                    <div
                        // @ts-expect-error
                        onClick={(e) => this.handleUndeleteAction(e, model)}
                        className={styles['undelete-link']}
                    >
                        Restore
                    </div>
                    <ActionMenu options={menuOption} />
                </div>
            );
        }
    };

    getModelForRow = (rowIndex: number): EventModel => {
        // @ts-expect-error
        return this.state.models[rowIndex];
    };

    handleFilterChange = (newFilter: TrashFilterOption) => {
        this.setState({selectedFilter: newFilter});
    };

    handleToggleSort = () => {
        this.setState((prevState) => ({
            sort: prevState.sort === 'change_time' ? '-change_time' : 'change_time',
        }));
    };

    handleUndeleteAction = (e: Event, columnData: EventModel) => {
        e.stopPropagation();

        const payload = columnData.payloads[0];

        if (payload) {
            $.ajax({
                type: 'POST',
                url: `${payload.href}/undelete.json`,
            }).then(() => {
                const unrestoredModels = this.state.models.filter((model) => {
                    return model.id !== columnData.id;
                });

                if (unrestoredModels) {
                    this.setState({
                        models: unrestoredModels,
                    });
                }
            });
        }
    };

    handleUndeleteAllAction = (e: Event) => {
        e.stopPropagation();

        const selectedFilter = this.state.selectedFilter;
        const sort = this.state.sort;

        this.setState({
            isLoading: true,
            models: [],
        });

        const query =
            selectedFilter && selectedFilter !== 'all'
                ? {sort, filter: {types: selectedFilter}}
                : {sort};

        api.post('events/restore-deleted', query).finally(() => {
            this.fetchItems(this.state.selectedFilter, this.state.sort);
        });
    };

    handlePermaneteDelete = (columnData: EventModel) => {
        const payload = columnData.payloads[0];

        const result = confirm(
            'Are you sure you want to permanently remove this contact? It cannot be undone!'
        );

        if (payload && result) {
            $.ajax({
                type: 'POST',
                url: `${payload.href}/harddelete`,
            }).then(() => {
                const undeletedModels = this.state.models.filter((model) => {
                    return model.id !== columnData.id;
                });

                if (undeletedModels) {
                    this.setState({
                        models: undeletedModels,
                    });
                }
            });
        }
    };
}
